import { Input, Select } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { checkMarkIcon, suffixIcon } from '../assets';
import CountryFlag from '../../countryFlag';
import { ValueSelect } from '../../customSelect';
import ErrorMessage from '../../errorMessage';
import InputTitle from '../../inputTitle';
import { usePhoneInput } from './usePhoneInput';
import type { IProps } from './types';
import style from './style.module.scss';

const { Option } = Select;

/**
 * Phone or tax number field with a country dial-code selector in front of the input. Takes countryData, selectedValue, handleChange and onChangeSelect from usePhoneInput, maps country or tax options into Select entries showing a CountryFlag with the dial code or label, falls back to a read-only country display unless the country is selectable, and appends an ErrorMessage on error.
 *
 * @param props - Field name and value, country and tax options and values, sizing and state flags, error message and the change, blur and key handlers.
 * @returns The rendered phone input wrapper element.
 */
const PhoneInput = (props: IProps): React.JSX.Element => {
  const { countryData, antSize, selectedValue, handleChange, onChangeSelect } = usePhoneInput({
    name: props.name,
    countryOptions: props.countryOptions,
    countryValue: props.countryValue,
    isObjectCountry: props.isObjectCountry,
    size: props.size,
    onChange: props.onChange,
    onChangeSelect: props.onChangeSelect,
  });

  const selectBefore = (
    <Select
      getPopupContainer={(trigger: any) => trigger.parentElement.parentElement.parentElement}
      popupMatchSelectWidth={false}
      menuItemSelectedIcon={<img width={16} height={16} src={checkMarkIcon} alt="" />}
      optionLabelProp="label"
      placeholder="Select..."
      value={props.isTaxInput ? props.taxValue : props.countryValue}
      suffixIcon={<img src={suffixIcon} alt="" width={16} height={16} />}
      size={antSize}
      onChange={onChangeSelect}
      aria-label="Country code flag selection"
    >
      {_.map(props.isTaxInput ? props.taxOptions : countryData, (x: ValueSelect, i) => (
        <Option
          key={`input-option-${i}`}
          value={x.value}
          option={x}
          className="phone-input-option"
          label={
            props.isTaxInput ? (
              <div className="custom-selection">
                <CountryFlag className="selection-flag" countryCode={selectedValue?.value} />
                <p>{x.label}</p>
              </div>
            ) : (
              <div className="custom-selection">
                <CountryFlag className="selection-flag" countryCode={selectedValue?.value} />
                <p>{`+${x.dial}`}</p>
              </div>
            )
          }
        >
          {props.isTaxInput ? (
            <p>{x.label}</p>
          ) : (
            <>
              <CountryFlag className="phone-input-option-flag" countryCode={x.value} />
              <p>{x.name}</p>
            </>
          )}
        </Option>
      ))}
    </Select>
  );

  const countryDisplay = (
    <div className="custom-selection">
      <CountryFlag className="selection-flag" countryCode={selectedValue?.value || ''} />
      <p>{`+${selectedValue?.dial || ''}`}</p>
    </div>
  );
  return (
    <div
      className={classNames(
        style.phoneInputWrapper,
        'phone-input-wrapper',
        props.className,
        style[`phoneInputSize--${props.size}`],
      )}
    >
      {props.title && <InputTitle title={props.title} isRequired={props.isRequired} />}
      <div className={classNames(style.phoneInputContainer)}>
        <Input
          addonBefore={props.couldSelectCountry || props.isTaxInput ? selectBefore : countryDisplay}
          value={props.value || ''}
          className={classNames(
            style.phoneInputContainerInput,
            'phone-number-input',
            props.isError ? 'phone-number-input-error' : '',
          )}
          disabled={props.disabled}
          placeholder={props.placeholder}
          name={props.name}
          aria-label={
            props['aria-label'] ?? (typeof props.title === 'string' ? props.title : undefined)
          }
          onChange={handleChange}
          onBlur={props.onBlur}
          maxLength={props.maxLength}
          size={antSize}
          onKeyDown={props.onKeyDown}
          id={props.id}
        />
      </div>
      {props.errorMessage && (
        <ErrorMessage className={style.errorMessage} errorMessage={props.errorMessage} hasIcon />
      )}
    </div>
  );
};

export default PhoneInput;
